import {
  reduce,
  flatMap,
  isEmpty,
  remove,
  map,
  find,
  keys
} from 'lodash'
import { commaFormatString } from '@/helpers'
import truncateAmount from '@/filters/truncateAmount'

let chartConfig = {
  bubbleSizes: {
    viewership: {
      min: 15,
      max: 90
    },
    dayParts: {
      min: 5,
      max: 30
    }
  }
}

const findMaxValue = function (data, propName) {
  // Make sure we always start the
  // reducer with a value from our data
  let startingPoint = 0
  if (Object.keys(data).length > 0) {
    startingPoint = data[keys(data)[0]][propName]
  }
  return reduce(data, (result, value) => {
    return result > value[propName] ? result : value[propName]
  }, startingPoint)
}

const findMinValue = function (data, propName) {
  // Make sure we always start the
  // reducer with a value from our data
  let startingPoint = 0
  if (Object.keys(data).length > 0) {
    startingPoint = data[keys(data)[0]][propName]
  }
  return reduce(data, (result, value) => {
    return result < value[propName] ? result : value[propName]
  }, startingPoint)
}

export const determineSizeOfBubble = function (data, audienceSize, bubbleSize) {
  const maxBubbleRadius = bubbleSize.max
  const minBubbleRadius = bubbleSize.min
  const maxValue = findMaxValue(data, 'size')
  const minValue = findMinValue(data, 'size')
  if (maxValue === minValue) {
    return bubbleSize.max
  } else {
    const valueRange = maxValue - minValue
    const bubbleSizeRange = maxBubbleRadius - minBubbleRadius
    // since radius is used as a key for finding the data associated with a bubble on the chart
    // we tak on a random number small enough to make them all different
    return (((audienceSize - minValue) / valueRange) * bubbleSizeRange) + minBubbleRadius
  }
}

export const transformHistoricalViewershipGraphData = function (networks, data, networksToHighlight, overrideBubbleSize) {
  if (overrideBubbleSize) {
    chartConfig.bubbleSizes.viewership = overrideBubbleSize
  }
  if (isEmpty(data)) {
    return {
      datasets: []
    }
  }
  return {
    datasets: map(data, (value, key) => {
      let network = find(networks, network => network.id === key)
      if (!network || !data.hasOwnProperty(network.id)) {
        network = {
          id: key,
          name: key
        }
      }
      let bgColor = network.wbd ? 'rgba(0, 82, 231, 0.8)' : 'rgba(255, 112, 0, 0.8)'
      let hoverBgColor = network.wbd ? 'rgba(76, 190, 231, 1)' : 'rgba(255, 194, 0, 1)'
      if (networksToHighlight.indexOf(network.id) > -1) {
        bgColor = hoverBgColor
      }
      return {
        network: network.id,
        label: `${network.name} - ${commaFormatString(String(data[network.id].size))}`,
        backgroundColor: bgColor,
        hoverBackgroundColor: hoverBgColor,
        dataLabels: {
          image: network.url,
          label: network.name,
          options: {
            color: '#fff'
          }
        },
        data: [
          {
            x: value.rating, // rating
            y: value.index, // index
            r: determineSizeOfBubble(data, value.size, chartConfig.bubbleSizes.viewership)
          }
        ],
        hoverRadius: 0
      }
    }).sort((a, b) => {
      // sort from small to large
      return a.data[0].r - b.data[0].r
    })
  }
}

const calcGraphAxises = function (data, xValue, yValue, shiftLeft) {
  const xMinValue = findMinValue(data, xValue)
  const xMaxValue = findMaxValue(data, xValue)
  const xValueRange = xMaxValue - xMinValue
  const shiftBy = shiftLeft ? xValueRange * shiftLeft : null
  const yMinValue = findMinValue(data, yValue)
  const yMaxValue = findMaxValue(data, yValue)
  const yValueRange = yMaxValue - yMinValue
  let xOffset = xValueRange * 0.2
  let yOffset = yValueRange * 0.2
  if (xOffset === 0) {
    xOffset = 0.5
  }
  if (yOffset === 0) {
    yOffset = 10
  }
  return {
    x: {
      min: xMinValue - (shiftBy ? xOffset - shiftBy : xOffset),
      max: xMaxValue + (shiftBy ? xOffset + shiftBy : xOffset)
    },
    y: {
      min: yMinValue - yOffset,
      max: yMaxValue + yOffset
    }
  }
}

export const topAttributesAxises = function (data) {
  return isEmpty(data) ? { x: { min: 0, max: 6 }, y: { min: 0, max: 1 } } : calcGraphAxises(data, 'coverage', 'index', 0.1)
}

export const historicalViewershipGraphAxises = function (data) {
  return isEmpty(data) ? { x: { min: 0, max: 1 }, y: { min: 0, max: 200 } } : calcGraphAxises(data, 'rating', 'index')
}

export const extractDayPartsData = function (data) {
  return remove(flatMap(data, network => {
    return [...network.day_parts]
  }), network => {
    return network.hasOwnProperty('index') && network.hasOwnProperty('rating')
  })
}

export const transformViewershipDataForDayParts = function (viewershipData, networks, config) {
  if (isEmpty(viewershipData) || isEmpty(networks)) {
    return networks.map(network => {
      return {
        network: network.id,
        name: network.name,
        data: {
          datasets: []
        }
      }
    })
  }
  return networks.map(network => {
    const data = viewershipData[network.id]
    if (!data) return null
    const allDayParts = extractDayPartsData(viewershipData)
    return {
      network: network.id,
      name: network.name,
      data: {
        datasets: data.day_parts.map(part => {
          const partData = data.day_parts.find(dayPart => dayPart.id === part.id)
          const dayPart = config.find(dayPart => dayPart.id === part.id)
          let bgColor = '#4CBEE7' // default
          if (dayPart && dayPart.color) {
            bgColor = dayPart.color
          }
          return {
            title: partData.id,
            label: partData.size,
            backgroundColor: bgColor, // this needs to come from the config
            data: [
              {
                x: partData.rating, // rating
                y: partData.index, // index
                r: determineSizeOfBubble(allDayParts, partData.size, chartConfig.bubbleSizes.dayParts)
              }
            ],
            hoverRadius: 0
          }
        }).sort((a, b) => {
          // sort from small to large
          return a.data[0].r - b.data[0].r
        })
      }
    }
  }).filter(n => n !== null)
}

export const transformUniverseDataForGraph = function (universeData) {
  if (!universeData || universeData && isEmpty(universeData.universe)) {
    return {
      labels: [],
      datasets: []
    }
  }
  const labels = universeData.universe.map(monthObj => {
    const date = new Date(monthObj.date)
    return date.toLocaleString('en-us', { month: 'short', year: '2-digit', timeZone: 'UTC' })
  })
  const chartData = universeData.universe.map(monthObj => monthObj.size)
  const backgroundColors = universeData.universe.map(bar => !bar.new ? 'rgba(0, 82, 231, 0.8)' : 'rgba(204, 104, 220, 0.8)')
  const hoverBackgroundColors = universeData.universe.map(bar => !bar.new ? 'rgba(76, 190, 231, 1)' : 'rgba(180, 64, 250, 1)')
  const data = {
    label: 'Audience Size',
    backgroundColor: backgroundColors,
    data: chartData,
    hoverBackgroundColor: hoverBackgroundColors
  }
  const datasets = [data]
  return {
    labels,
    datasets
  }
}

export const transformTopAttributesForGraph = function (attributeData, bubbleSizeRange) {
  if (!bubbleSizeRange) {
    bubbleSizeRange = { min: 15, max: 90 }
  }
  return attributeData ? {
    datasets: attributeData.map(attribute => {
      const { id, ...attr } = attribute.attribute
      return {
        label: attribute.attribute.parent + ' ' + attribute.attribute.name,
        id: attribute.id,
        attribute: attr,
        backgroundColor: 'rgba(0, 82, 231, 0.8)',
        hoverBackgroundColor: 'rgba(76, 190, 231, 1)',
        data: [
          {
            x: attribute.coverage, // coverage
            y: attribute.index, // index
            r: determineSizeOfBubble(attributeData, attribute.size, bubbleSizeRange)
          }
        ],
        dataLabels: {
          label: truncateAmount(attribute.size),
          rightLabel: attribute.attribute.parent + ' ' + attribute.attribute.name
        },
        hoverRadius: 0
      }
    })
  } : {
    datasets: []
  }
}

// engagement:32
// id:1
// name:"NCAA"
// reach:1609

export const transformReachDataForGraph = function (data) {
  if (!data) {
    return {
      labels: [],
      datasets: []
    }
  }
  const labels = data.map(brandData => {
    return brandData.name
  })
  const engagementData = data.map(brandData => {
    return brandData.engagement
  })
  const reachData = data.map(brandData => {
    return brandData.reach
  })
  return {
    labels,
    datasets: [
      {
        label: 'Engagement',
        backgroundColor: 'rgba(0, 82, 231, 0.4)',
        data: engagementData,
        stack: 'stack 0'
      },
      {
        label: 'Reach',
        backgroundColor: 'rgba(0, 82, 231, 1)',
        data: reachData,
        stack: 'stack 1'
      }
    ]
  }
}




// WEBPACK FOOTER //
// ./src/app/appComponents/workbench/lasGraphs/chartDataHelpers.js